import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete, Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  IconButton, MenuItem, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Check as CheckIcon, ContentCopy as CopyIcon, Delete as DeleteIcon, Groups as MembersIcon, Image as ImageIcon,
  Lock as LockIcon, Public as PublicIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import { refreshMyLabs } from 'shared/hooks/useMyLabs';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { LAB_COLORS, coverBackground, labColor } from './labTheme';
import LabMarkdown from './LabMarkdown';

const thisYear = new Date().getFullYear();

/** Markdown text with Write / Preview tabs (the lab description). */
export function MarkdownField({ label, value, onChange }) {
  const [tab, setTab] = useState('write');
  return (
    <Box>
      <Box display="flex" alignItems="center" gap={0.5} mb={0.75}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{label}</Typography>
        {['write', 'preview'].map((t) => (
          <Button key={t} size="small" variant={tab === t ? 'contained' : 'text'} disableElevation
            onClick={() => setTab(t)} sx={{ minWidth: 0, px: 1.25, py: 0.25, textTransform: 'none' }}>
            {t === 'write' ? 'Write' : 'Preview'}
          </Button>
        ))}
      </Box>
      {tab === 'write' ? (
        <TextField fullWidth multiline minRows={6} value={value || ''} onChange={(e) => onChange(e.target.value)}
          placeholder={'## Our research\nWe study **soil and gut microbiomes**…\n\n- Metagenomics\n- [Our website](https://…)'}
          helperText="Markdown: ## heading, **bold**, *italic*, - list, 1. list, [link](https://…), > quote"
          slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: '0.9em' } } }} />
      ) : (
        <Box sx={{ minHeight: 140, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          {value?.trim() ? <LabMarkdown>{value}</LabMarkdown> : <Typography color="text.secondary" variant="body2">Nothing to preview yet.</Typography>}
        </Box>
      )}
    </Box>
  );
}

/** Keyword chips: type and press Enter (or comma) to add. */
function KeywordsField({ value, onChange }) {
  return (
    <Autocomplete
      multiple freeSolo options={[]} value={value}
      onChange={(_, v) => onChange(v.map((x) => String(x).trim()).filter(Boolean))}
      renderTags={(v, getTagProps) => v.map((k, i) => <Chip size="small" label={k} {...getTagProps({ index: i })} key={k} />)}
      renderInput={(params) => (
        <TextField {...params} label="Keywords" placeholder="Type a keyword and press Enter"
          onKeyDown={(e) => {
            if (e.key === ',' && e.target.value.trim()) {
              e.preventDefault();
              onChange([...value, e.target.value.trim()]);
              e.target.value = '';
            }
          }} />
      )}
    />
  );
}

function ColorPicker({ value, onChange }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">Page colour</Typography>
      <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mt={0.5}>
        {LAB_COLORS.map((c) => (
          <Box key={c} role="button" aria-label={`Colour ${c}`} onClick={() => onChange(c)}
            sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: c, cursor: 'pointer', display: 'grid', placeItems: 'center',
              boxShadow: value === c ? `0 0 0 2px #fff, 0 0 0 4px ${c}` : 'none' }}>
            {value === c && <CheckIcon sx={{ color: '#fff', fontSize: 18 }} />}
          </Box>
        ))}
        <Tooltip title="Any colour">
          <Box component="input" type="color" value={value} onChange={(e) => onChange(e.target.value)}
            sx={{ width: 34, height: 34, border: 'none', p: 0, bgcolor: 'transparent', cursor: 'pointer' }} />
        </Tooltip>
      </Box>
    </Box>
  );
}

/** Create a lab (the creator becomes the PI) — only in an organization the user is affiliated with. */
export function CreateLabDialog({ open, onClose, onCreated }) {
  const [orgs, setOrgs] = useState(null);
  const [form, setForm] = useState({ name: '', organization: '', description: '', keywords: [], background_color: LAB_COLORS[0] });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    axiosService.get('/consortium/labs/my-organizations/')
      .then((res) => {
        setOrgs(res.data || []);
        if (res.data?.length) setForm((f) => ({ ...f, organization: f.organization || res.data[0].id }));
      })
      .catch(() => setOrgs([]));
  }, [open]);

  const create = async () => {
    setSaving(true); setError(null);
    try {
      const res = await axiosService.post('/consortium/labs/', form);
      refreshMyLabs();                                  // the new lab appears in the sidebar
      onCreated(res.data);
      setForm({ name: '', organization: '', description: '', keywords: [], background_color: LAB_COLORS[0] });
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create the lab.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create a lab</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You&apos;ll be the lab&apos;s principal investigator. You can add members, news, positions and projects next.
        </Typography>
        {orgs && orgs.length === 0 ? (
          <Typography color="warning.main" variant="body2">
            A lab belongs to an organization. Add your affiliation on your profile first, then create the lab there.
          </Typography>
        ) : (
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField label="Lab name" required value={form.name} autoFocus
              onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Soil Microbiome Lab" />
            <TextField select label="Organization" required value={form.organization}
              onChange={(e) => setForm({ ...form, organization: e.target.value })}>
              {(orgs || []).map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
            </TextField>
            <MarkdownField label="Description" value={form.description}
              onChange={(description) => setForm({ ...form, description })} />
            <KeywordsField value={form.keywords} onChange={(keywords) => setForm({ ...form, keywords })} />
            <ColorPicker value={form.background_color} onChange={(background_color) => setForm({ ...form, background_color })} />
          </Stack>
        )}
        {error && <Typography color="error" variant="body2" sx={{ mt: 2 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={create} disabled={saving || !form.name.trim() || !form.organization}>Create lab</Button>
      </DialogActions>
    </Dialog>
  );
}

const VISIBILITY = [
  { value: 'members', icon: LockIcon, label: 'Lab members only', help: 'Hidden from the Collaboration Hub, search and feeds.' },
  { value: 'platform', icon: MembersIcon, label: 'Everyone on Otable', help: 'Signed-in Otable users can find, follow and contact the lab.' },
  { value: 'public', icon: PublicIcon, label: 'Public', help: 'Also a public page anyone can open with the link, no account needed.' },
];
const PUBLIC_SECTION_LABELS = [['about', 'About'], ['members', 'Members'], ['news', 'News'], ['projects', 'Active projects'],
  ['publications', 'Publications'], ['theses', 'Theses'], ['positions', 'Open positions'], ['alumni', 'Alumni']];

/** Who can see the lab page: three layers, chosen by the PI; public pages show the chosen sections. */
function VisibilityPicker({ lab, value, sections, onChange, onSections }) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/labs/${lab.slug || lab.id}/`;
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Who can see this page</Typography>
      <Stack spacing={1}>
        {VISIBILITY.map(({ value: v, icon: Icon, label, help }) => (
          <Box key={v} role="radio" aria-checked={value === v} tabIndex={0} onClick={() => onChange(v)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onChange(v); }}
            sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', p: 1.25, borderRadius: 1.5, cursor: 'pointer', border: '1px solid',
              borderColor: value === v ? 'primary.main' : 'divider', bgcolor: value === v ? 'action.selected' : 'transparent' }}>
            <Icon sx={{ fontSize: 20, mt: 0.25, color: value === v ? 'primary.main' : 'text.secondary' }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
              <Typography variant="caption" color="text.secondary">{help}</Typography>
            </Box>
          </Box>
        ))}
      </Stack>
      {value === 'public' && (
        <Box sx={{ mt: 1.5, pl: 0.5 }}>
          <Typography variant="caption" color="text.secondary">Shown on the public page</Typography>
          <Box display="flex" flexWrap="wrap" gap={0.75} mt={0.5}>
            {PUBLIC_SECTION_LABELS.map(([k, l]) => {
              const on = sections.includes(k);
              return <Chip key={k} label={l} size="small" clickable color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'}
                icon={on ? <CheckIcon /> : undefined}
                onClick={() => onSections(on ? sections.filter((x) => x !== k) : [...sections, k])} />;
            })}
          </Box>
          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1 }}>
            Never public: followers, statistics, applications and the team chat. Members&apos; email addresses aren&apos;t shown.
          </Typography>
          {lab.visibility === 'public' ? (
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <TextField size="small" fullWidth value={link} slotProps={{ htmlInput: { readOnly: true } }} />
              <Tooltip title={copied ? 'Copied' : 'Copy link'}>
                <IconButton onClick={() => { navigator.clipboard?.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Save to get the public link.</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

/** Name, description, keywords, website, colour and cover image. The PI can also delete the lab. */
export function LabEditDialog({ open, onClose, lab, onSaved, onDeleted, isPI }) {
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open && lab) {
      setForm({ name: lab.name, description: lab.description, keywords: lab.keywords || [], website: lab.website || '',
        background_color: labColor(lab), visibility: lab.visibility || 'platform', slug: lab.slug || '',
        public_sections: lab.public_sections || PUBLIC_SECTION_LABELS.map(([k]) => k) });
      setError(null); setConfirmDelete(false);
    }
  // only when the dialog opens: a background refresh of the lab must not wipe what's being typed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lab?.id]);

  if (!form) return null;
  const run = async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); } catch (err) { setError(apiErrorMessage(err)); } finally { setBusy(false); }
  };
  const save = () => run(async () => {
    const body = { ...form };
    if (!isPI) { delete body.visibility; delete body.public_sections; delete body.slug; }   // the PI decides these
    if (isPI && body.slug === lab.slug) delete body.slug;
    onSaved((await axiosService.patch(`/consortium/labs/${lab.id}/`, body)).data);
    onClose();
  });
  const upload = (file) => run(async () => {
    const fd = new FormData();
    fd.append('cover', file);
    onSaved((await axiosService.post(`/consortium/labs/${lab.id}/cover/`, fd)).data);
  });
  const removeCover = () => run(async () => onSaved((await axiosService.delete(`/consortium/labs/${lab.id}/cover/`)).data));
  const remove = () => run(async () => { await axiosService.delete(`/consortium/labs/${lab.id}/`); refreshMyLabs(); onDeleted(); });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit lab</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Box sx={{ height: 110, borderRadius: 1, background: coverBackground({ ...lab, background_color: form.background_color }),
            display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', p: 1, gap: 1 }}>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png" hidden
              onChange={(e) => { if (e.target.files?.[0]) upload(e.target.files[0]); e.target.value = ''; }} />
            <Button size="small" variant="contained" color="inherit" startIcon={<ImageIcon />} disabled={busy}
              onClick={() => fileRef.current?.click()} sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}>
              {lab.cover_url ? 'Change cover' : 'Add cover image'}
            </Button>
            {lab.cover_url && (
              <Button size="small" variant="contained" color="inherit" disabled={busy} onClick={removeCover}
                sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}>Remove</Button>
            )}
          </Box>
          <TextField label="Lab name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <MarkdownField label="Description" value={form.description}
            onChange={(description) => setForm({ ...form, description })} />
          <KeywordsField value={form.keywords} onChange={(keywords) => setForm({ ...form, keywords })} />
          <TextField label="Website" value={form.website} placeholder="https://"
            onChange={(e) => setForm({ ...form, website: e.target.value })} />
          <ColorPicker value={form.background_color} onChange={(background_color) => setForm({ ...form, background_color })} />
          {isPI && (
            <TextField label="Web address" value={form.slug} placeholder="your-lab-name"
              onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+/, '') })}
              helperText="Letters, numbers and hyphens. The public page lives at this address."
              slotProps={{ input: { startAdornment: <Typography color="text.secondary" sx={{ whiteSpace: 'nowrap', mr: 0.25 }}>{window.location.host}/labs/</Typography> } }} />
          )}
          {isPI && (
            <VisibilityPicker lab={lab} value={form.visibility} sections={form.public_sections}
              onChange={(visibility) => setForm({ ...form, visibility })}
              onSections={(public_sections) => setForm({ ...form, public_sections })} />
          )}
        </Stack>
        {error && <Typography color="error" variant="body2" sx={{ mt: 2 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {isPI ? (
          confirmDelete ? (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="error">Delete the lab and its page for everyone?</Typography>
              <Button color="error" variant="contained" size="small" onClick={remove} disabled={busy}>Delete</Button>
              <Button size="small" onClick={() => setConfirmDelete(false)}>Keep</Button>
            </Box>
          ) : <Button color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)}>Delete lab</Button>
        ) : <span />}
        <Box>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={busy || !form.name.trim()}>Save</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

/** Add or edit a member or alumnus. Members are Otable users; alumni may also be just a name. */
export function MemberDialog({ open, onClose, lab, member, alumni, isPI, onSaved }) {
  const [userOptions, setUserOptions] = useState([]);
  const [query, setQuery] = useState('');
  const [form, setForm] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const editing = Boolean(member);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setForm(member ? {
      lab_title: member.lab_title || '', is_alumni: member.is_alumni, start_year: member.start_year || '',
      end_year: member.end_year || '', current_position: member.current_position || '', can_edit: member.can_edit,
      ...(member.user ? {} : { email: member.email || '' }),
    } : { user: null, name: '', email: '', lab_title: '', is_alumni: Boolean(alumni), start_year: '', end_year: alumni ? thisYear : '',
      current_position: '', can_edit: false });
  }, [open, member, alumni]);

  useEffect(() => {
    if (!open || editing || query.trim().length < 2) { setUserOptions([]); return undefined; }
    const t = setTimeout(() => {
      axiosService.get(`/users/?search=${encodeURIComponent(query.trim())}&page_size=10`)
        .then((res) => setUserOptions(res.data?.results || res.data || []))
        .catch(() => setUserOptions([]));
    }, 300);
    return () => clearTimeout(t);
  }, [query, open, editing]);

  const save = async () => {
    setBusy(true); setError(null);
    const body = { ...form, start_year: form.start_year || null, end_year: form.end_year || null };
    if (!editing) {
      body.user = form.user?.id || null;
      if (body.user) { delete body.name; delete body.email; }
    } else {
      delete body.user;
    }
    if (!isPI) delete body.can_edit;
    try {
      const res = editing
        ? await axiosService.patch(`/consortium/labs/${lab.id}/members/${member.id}/`, body)
        : await axiosService.post(`/consortium/labs/${lab.id}/members/`, body);
      onSaved(res.data);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const isPIRow = member && String(member.user) === String(lab?.pi);
  const userName = (u) => u?.name || [u?.first_name, u?.last_name].filter(Boolean).join(' ') || u?.username || '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{editing ? `Edit ${member.display_name}` : form.is_alumni ? 'Add an alumnus' : 'Invite a lab member'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {!editing && (
            <Autocomplete
              options={userOptions} value={form.user || null} filterOptions={(x) => x}
              onChange={(_, v) => setForm({ ...form, user: v })}
              onInputChange={(_, v) => setQuery(v)}
              getOptionLabel={userName}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderOption={(props, u) => (
                <Box component="li" {...props} key={u.id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Avatar src={u.avatar || undefined} sx={{ width: 26, height: 26 }}>{userName(u)[0]}</Avatar>
                  <Box>
                    <Typography variant="body2">{userName(u)}</Typography>
                    <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => <TextField {...params} label="Person on Otable" placeholder="Search by name or email" />}
            />
          )}
          {!editing && form.is_alumni && !form.user && (
            <TextField label="…or their name (not on Otable)" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          )}
          {((!editing && form.is_alumni && !form.user) || (editing && member && !member.user)) && (
            <TextField label="Their email (optional)" type="email" value={form.email || ''}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              helperText={editing && member.email_invited_at && form.email === member.email
                ? `Invited to Otable by email on ${new Date(member.email_invited_at).toLocaleDateString()}. When they sign up with this address, the listing links to their profile.`
                : 'We email them once, inviting them to join Otable. When they sign up with this address, the listing links to their profile. Only the lab’s editors see it.'} />
          )}
          {!editing && form.user && (
            <Typography variant="caption" color="text.secondary">
              They get an invitation and appear on the lab only after they accept.
            </Typography>
          )}
          <TextField label="Title in the lab" value={form.lab_title || ''} placeholder="e.g. PhD student, Lab manager"
            helperText="Can differ from the title on their profile"
            onChange={(e) => setForm({ ...form, lab_title: e.target.value })} />
          <Box display="flex" gap={2}>
            <TextField label="From (year)" type="number" value={form.start_year || ''}
              onChange={(e) => setForm({ ...form, start_year: e.target.value })} />
            {form.is_alumni && (
              <TextField label="Until (year)" type="number" value={form.end_year || ''}
                onChange={(e) => setForm({ ...form, end_year: e.target.value })} />
            )}
          </Box>
          {form.is_alumni && (
            <TextField label="Where are they now?" value={form.current_position || ''} placeholder="e.g. Postdoc at EMBL"
              onChange={(e) => setForm({ ...form, current_position: e.target.value })} />
          )}
          {editing && !isPIRow && (
            <FormControlLabel control={<Switch checked={Boolean(form.is_alumni)}
              onChange={(e) => setForm({ ...form, is_alumni: e.target.checked, end_year: e.target.checked ? (form.end_year || thisYear) : form.end_year })} />}
              label="Alumnus (has left the lab)" />
          )}
          {isPI && !isPIRow && !form.is_alumni && (
            <FormControlLabel control={<Switch checked={Boolean(form.can_edit)}
              onChange={(e) => setForm({ ...form, can_edit: e.target.checked })} />}
              label="May edit the lab page" />
          )}
        </Stack>
        {error && <Typography color="error" variant="body2" sx={{ mt: 2 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save}
          disabled={busy || (!editing && !form.user && !(form.is_alumni && form.name?.trim()))}>
          {editing ? 'Save' : form.user ? 'Send invitation' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Pick any Otable user (or nobody). VALUE / onChange use {id, name, avatar_url}. */
export function UserPicker({ label, value, onChange, helperText }) {
  const [options, setOptions] = useState([]);
  const [query, setQuery] = useState('');
  useEffect(() => {
    if (query.trim().length < 2) { setOptions([]); return undefined; }
    const t = setTimeout(() => {
      axiosService.get(`/users/?search=${encodeURIComponent(query.trim())}&page_size=10`)
        .then((res) => setOptions((res.data?.results || res.data || []).map((u) => ({
          id: u.id, name: u.name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username,
          avatar_url: u.avatar || null, email: u.email,
        }))))
        .catch(() => setOptions([]));
    }, 300);
    return () => clearTimeout(t);
  }, [query]);
  return (
    <Autocomplete
      options={options} value={value || null} filterOptions={(x) => x}
      onChange={(_, v) => onChange(v)} onInputChange={(_, v) => setQuery(v)}
      getOptionLabel={(u) => u?.name || ''} isOptionEqualToValue={(a, b) => a.id === b.id}
      renderOption={(props, u) => (
        <Box component="li" {...props} key={u.id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Avatar src={u.avatar_url || undefined} sx={{ width: 26, height: 26 }}>{(u.name || '?')[0]}</Avatar>
          <Box>
            <Typography variant="body2">{u.name}</Typography>
            {u.email && <Typography variant="caption" color="text.secondary">{u.email}</Typography>}
          </Box>
        </Box>
      )}
      renderInput={(params) => <TextField {...params} label={label} placeholder="Search by name or email" helperText={helperText} />}
    />
  );
}

// Fields of the news / positions / projects editor
export const ITEM_FIELDS = {
  news: {
    label: 'news item',
    fields: [
      { key: 'title', label: 'Headline', required: true, maxLength: 120 },
      { key: 'person', label: 'Who is it about? (optional)', user: true,
        helperText: 'Their photo appears next to the news. Leave empty and the lab’s initials are shown.' },
      { key: 'date', label: 'Date', type: 'date' },
      { key: 'body', label: 'Text', multiline: true, maxLength: 600 },
      { key: 'link', label: 'Link', placeholder: 'https://' },
    ],
  },
  positions: {
    label: 'open position',
    fields: [
      { key: 'title', label: 'Position', required: true, placeholder: 'e.g. PhD position in soil metagenomics' },
      { key: 'kind', label: 'Type', select: [['phd', 'PhD'], ['postdoc', 'Postdoc'], ['masters', "Master's"], ['ra', 'Research assistant'], ['technician', 'Technician'], ['intern', 'Intern'], ['other', 'Other']] },
      { key: 'description', label: 'Description', multiline: true },
      { key: 'deadline', label: 'Application deadline', type: 'date' },
      { key: 'apply_url', label: 'How to apply (link)', placeholder: 'https://' },
      { key: 'contact_email', label: 'Contact email' },
      { key: 'is_open', label: 'Open for applications', switch: true },
    ],
  },
  theses: {
    label: 'thesis', contribution: true,
    fields: [
      { key: 'title', label: 'Thesis title', required: true },
      { key: 'degree', label: 'Degree', select: [['phd', 'PhD thesis'], ['masters', "Master's thesis"], ['undergraduate', 'Undergraduate thesis']] },
      { key: 'author', label: 'Author on Otable', user: true, helperText: 'Or type the name below if they are not on Otable.' },
      { key: 'author_name', label: 'Author name (if not on Otable)' },
      { key: 'year', label: 'Year', type: 'number' },
      { key: 'supervisor', label: 'Supervisor(s)' },
      { key: 'institution', label: 'University / institute' },
      { key: 'url', label: 'Link to the thesis', placeholder: 'https://' },
      { key: 'abstract', label: 'Abstract', multiline: true },
    ],
  },
  publications: {
    label: 'publication', contribution: true,
    fields: [
      { key: 'title', label: 'Title', required: true },
      { key: 'authors', label: 'Authors', placeholder: 'e.g. Aydın Ş, Şanlı K, …' },
      { key: 'journal', label: 'Journal / venue' },
      { key: 'year', label: 'Year', type: 'number' },
      { key: 'doi', label: 'DOI', placeholder: '10.…' },
      { key: 'url', label: 'Link', placeholder: 'https://' },
    ],
  },
  projects: {
    label: 'research project',
    fields: [
      { key: 'title', label: 'Project title', required: true },
      { key: 'acronym', label: 'Acronym' },
      { key: 'funder', label: 'Funder', placeholder: 'e.g. Horizon Europe, TÜBİTAK' },
      { key: 'grant_number', label: 'Grant number' },
      { key: 'role', label: 'Lab role', placeholder: 'e.g. Coordinator, Partner' },
      { key: 'start_date', label: 'Start', type: 'date' },
      { key: 'end_date', label: 'End', type: 'date' },
      { key: 'url', label: 'Website', placeholder: 'https://' },
      { key: 'description', label: 'Description', multiline: true },
    ],
  },
};

export function ItemDialog({ open, onClose, lab, kind, item, onSaved, isPI }) {
  const conf = ITEM_FIELDS[kind];
  const [form, setForm] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !conf) return;
    setError(null);
    const fields = conf.fields;
    const blank = Object.fromEntries(fields.map((f) => [f.key, f.switch ? true : f.select ? f.select[0][0] : '']));
    if (kind === 'news') blank.date = new Date().toISOString().slice(0, 10);
    const fromItem = item ? Object.fromEntries(fields.map((f) => [f.key, item[f.key] ?? blank[f.key]])) : {};
    if (item && kind === 'news') fromItem.person = item.person_detail || null;   // the picker works with objects
    if (item && kind === 'theses') fromItem.author = item.author_detail || null;
    const pickers = Object.fromEntries(conf.fields.filter((f) => f.user).map((f) => [f.key, null]));
    setForm({ ...blank, ...pickers, ...fromItem });
  }, [open, item, kind, conf]);


  if (!conf) return null;
  const tooLong = conf.fields.some((f) => f.maxLength && String(form[f.key] || '').length > f.maxLength);
  const save = async () => {
    setBusy(true); setError(null);
    const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === '' && conf.fields.find((f) => f.key === k)?.type === 'date' ? null : v]));
    conf.fields.filter((f) => f.user).forEach((f) => { body[f.key] = body[f.key]?.id || null; });
    if ('year' in body) body.year = body.year ? Number(body.year) : null;
    try {
      const url = `/consortium/labs/${lab.id}/${conf.contribution ? 'contributions' : 'items'}/${kind}/`;
      const res = item ? await axiosService.patch(`${url}${item.id}/`, body) : await axiosService.post(url, body);
      onSaved(res.data);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{item ? 'Edit' : 'Add'} {conf.label}</DialogTitle>
      <DialogContent>
        {conf.contribution && !isPI && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The PI confirms it before it appears on the lab page.
          </Typography>
        )}
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {conf.fields.map((f) => (f.user ? (
            <UserPicker key={f.key} label={f.label} helperText={f.helperText} value={form[f.key]}
              onChange={(v) => setForm({ ...form, [f.key]: v })} />
          ) : f.switch ? (
            <FormControlLabel key={f.key} label={f.label}
              control={<Switch checked={Boolean(form[f.key])} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} />} />
          ) : (
            <TextField
              key={f.key} label={f.label} required={f.required} placeholder={f.placeholder}
              select={Boolean(f.select)} multiline={f.multiline} minRows={f.multiline ? 3 : undefined}
              type={f.type || 'text'} value={form[f.key] ?? ''}
              error={Boolean(f.maxLength) && String(form[f.key] || '').length > f.maxLength}
              helperText={f.maxLength ? `${String(form[f.key] || '').length} / ${f.maxLength}` : undefined}
              slotProps={f.type === 'date' ? { inputLabel: { shrink: true } } : undefined}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
            >
              {f.select && f.select.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
            </TextField>
          )))}
        </Stack>
        {error && <Typography color="error" variant="body2" sx={{ mt: 2 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || tooLong || !String(form.title || '').trim()}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export function ConfirmIconDelete({ onConfirm, label }) {
  const [armed, setArmed] = useState(false);
  return armed ? (
    <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
      <Button size="small" color="error" onClick={() => { setArmed(false); onConfirm(); }} sx={{ minWidth: 0, py: 0 }}>Delete</Button>
      <Button size="small" onClick={() => setArmed(false)} sx={{ minWidth: 0, py: 0 }}>Keep</Button>
    </Box>
  ) : (
    <Tooltip title={label || 'Delete'}>
      <IconButton size="small" onClick={() => setArmed(true)} aria-label={label || 'Delete'}><DeleteIcon sx={{ fontSize: 17 }} /></IconButton>
    </Tooltip>
  );
}
